import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import CurrencySelector from "@/components/CurrencySelector";
import LanguageSelector from "@/components/LanguageSelector";
import MobileMenu from "@/components/MobileMenu";
import { CitySearchPanel } from "@/components/CitySearchCombobox";
import { CITIES } from "@/cityConfig";

export const Header = ({ settings, currentCity, onCityChange, selectedCurrency, onCurrencyChange, lang, onLangChange, t, availableLanguages, solid = false }) => {
  const [isScrolled, setIsScrolled] = useState(solid);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCityDropdown, setIsCityDropdown] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(solid || window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [solid]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsCityDropdown(false);
        setActiveCountry(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  const handleCitySelect = (slug) => {
    setIsCityDropdown(false);
    setActiveCountry(null);
    setIsMobileMenuOpen(false);
    const keepLang = lang && lang !== "ru" && (CITIES[slug]?.languages || ["ru", "en"]).includes(lang);
    navigate(`/${slug}${keepLang ? `?lang=${lang}` : ""}`);
  };

  const navBtnClass = `font-semibold transition-colors ${isScrolled ? 'text-gray-700 hover:text-pink-500' : 'text-white hover:text-pink-300'}`;

  return (
    <header
      className={`top-0 left-0 right-0 z-50 transition-all duration-300 md:fixed ${
        isScrolled ? "md:bg-white/95 md:backdrop-blur-md md:shadow-lg" : "md:bg-transparent"
      } ${isMobileMenuOpen ? "fixed bg-white/95 backdrop-blur-md shadow-lg" : "absolute bg-transparent"}`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href={`/${currentCity?.slug || "antalya"}`} className="flex items-baseline" data-testid="logo">
            <Logo variant={(isScrolled || isMobileMenuOpen) ? "dark" : "light"} />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("catalog")} className={navBtnClass}>{t("nav.catalog")}</button>
            <button onClick={() => scrollToSection("advantages")} className={navBtnClass}>{t("nav.advantages")}</button>
            <button onClick={() => scrollToSection("reviews")} className={navBtnClass}>{t("nav.reviews")}</button>
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Desktop: Currency + Language + City + Phone */}
            <div className="hidden sm:block">
              <CurrencySelector selectedCurrency={selectedCurrency} onCurrencyChange={onCurrencyChange} isScrolled={isScrolled} />
            </div>
            <div className="hidden sm:block">
              <LanguageSelector selectedLang={lang} onLangChange={onLangChange} isScrolled={isScrolled} availableLanguages={availableLanguages} />
            </div>

            {/* Desktop City Selector */}
            <DesktopCityDropdown
              ref={dropdownRef}
              currentCity={currentCity}
              isScrolled={isScrolled}
              isCityDropdown={isCityDropdown}
              setIsCityDropdown={setIsCityDropdown}
              activeCountry={activeCountry}
              setActiveCountry={setActiveCountry}
              onCitySelect={handleCitySelect}
              lang={lang}
            />

            {/* Phone */}
            <a
              href={`tel:${settings?.whatsapp || "+905316224378"}`}
              className={`hidden lg:flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold transition-all ${
                isScrolled ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:shadow-lg' : 'bg-white text-pink-600 hover:shadow-lg'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span>{settings?.whatsapp || "+90 531 622 43 78"}</span>
            </a>

            {/* Mobile: Currency + Burger */}
            <div className="md:hidden">
              <CurrencySelector selectedCurrency={selectedCurrency} onCurrencyChange={onCurrencyChange} isScrolled={isScrolled || isMobileMenuOpen} />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-full transition-colors ${
                (isScrolled || isMobileMenuOpen) ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/20'
              }`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <MobileMenu
            currentCity={currentCity}
            onCitySelect={handleCitySelect}
            scrollToSection={scrollToSection}
            selectedCurrency={selectedCurrency}
            onCurrencyChange={onCurrencyChange}
            lang={lang}
            onLangChange={onLangChange}
            settings={settings}
            t={t}
            availableLanguages={availableLanguages}
          />
        )}
      </div>
    </header>
  );
};

/* Desktop City Dropdown — extracted for clarity */
import { forwardRef } from "react";

const DesktopCityDropdown = forwardRef(({ currentCity, isScrolled, isCityDropdown, setIsCityDropdown, activeCountry, setActiveCountry, onCitySelect, lang }, ref) => (
  <div className="relative hidden sm:block" ref={ref}>
    <button
      onClick={() => { setIsCityDropdown(!isCityDropdown); setActiveCountry(null); }}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full transition-all ${
        isScrolled ? 'bg-pink-100 text-pink-600 hover:bg-pink-200' : 'bg-white/20 text-white hover:bg-white/30'
      }`}
      data-testid="city-selector"
    >
      <MapPin className="w-4 h-4" />
      <span className="font-semibold">{lang === "tr" ? (currentCity?.nameTr || currentCity?.nameEn) : (lang === "en" ? currentCity?.nameEn : currentCity?.name || "Город")}</span>
      <ChevronDown className={`w-3 h-3 transition-transform ${isCityDropdown ? "rotate-180" : ""}`} />
    </button>

    {isCityDropdown && (
      <div
        className="absolute top-full mt-2 right-0 w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in"
        data-testid="header-city-dropdown"
      >
        <CitySearchPanel
          lang={lang}
          selectedSlug={currentCity?.slug}
          onSelect={(city) => onCitySelect(city.slug)}
          autoFocus
          compact
          listMaxClass="max-h-[min(60vh,400px)]"
          testIdPrefix="city-option"
        />
      </div>
    )}
  </div>
));

export default Header;
